import { useMemo, useState } from 'react';
import {
  CatalogProductBulkImportBodyParams,
  CustomFieldDefinition,
} from 'src/api/generated';
import {
  autoMapCustomFields,
  autoMapProductFields,
  buildProductPayload,
  parseProductFile,
  ParsedRow,
  ProductField,
} from '../utils/product-import.utils';

const MAX_ITEMS = 1000;

/**
 * State for the product import hook.
 * Tracks file metadata, parsed data, field mappings, and any parsing errors.
 *
 * @property fileName - The name of the currently loaded import file
 * @property headers - Column headers parsed from the import file
 * @property rows - All rows parsed from the import file
 * @property previewRows - First 5 rows for preview display
 * @property fieldMapping - Mapping of standard product fields to their column headers
 * @property customFieldMapping - Mapping of custom field IDs to their column headers
 * @property parseError - Error message if file parsing failed
 */
export interface UseProductImportState {
  fileName: string | null;
  headers: string[];
  rows: ParsedRow[];
  previewRows: ParsedRow[];
  fieldMapping: Partial<Record<ProductField, string | undefined>>;
  customFieldMapping: Record<number, string | undefined>;
  parseError: string | null;
}

/**
 * Actions provided by the product import hook.
 * Handles file parsing, field mapping, and data validation.
 *
 * @property handleFileChange - Processes a selected import file and extracts headers/rows
 * @property reset - Clears all state back to initial values
 * @property buildItems - Constructs import payload items from current state
 * @property validateRowLimit - Checks if row count exceeds maximum allowed items
 * @property setFieldMapping - Updates the standard product field mapping
 * @property setCustomFieldMapping - Updates the custom field mapping
 * @property setParseError - Sets or clears the parse error message
 */
export interface UseProductImportActions {
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  reset: () => void;
  buildItems: () => CatalogProductBulkImportBodyParams['items'];
  validateRowLimit: () => string | null;
  setFieldMapping: (
    updater: (
      prev: Partial<Record<ProductField, string | undefined>>
    ) => Partial<Record<ProductField, string | undefined>>
  ) => void;
  setCustomFieldMapping: (
    updater: (
      prev: Record<number, string | undefined>
    ) => Record<number, string | undefined>
  ) => void;
  setParseError: (value: string | null) => void;
}

/**
 * Hook for managing product file imports with field mapping and validation.
 *
 * Provides functionality to:
 * - Parse Excel/CSV files and extract headers and rows
 * - Auto-map standard and custom fields based on headers
 * - Build import payloads with field mappings
 * - Validate row counts against limits
 *
 * @param customFieldDefinitions - Array of custom field definitions for auto-mapping
 * @returns Tuple of [state, actions] following the reducer pattern
 *
 * @example
 * const [state, actions] = useProductImport(customFieldDefs);
 * // state contains fileName, headers, rows, fieldMapping, etc.
 * // actions contain handleFileChange, buildItems, validateRowLimit, etc.
 */
export const useProductImport = (
  customFieldDefinitions: CustomFieldDefinition[]
): [UseProductImportState, UseProductImportActions] => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fieldMapping, setFieldMapping] = useState<
    Partial<Record<ProductField, string | undefined>>
  >({});
  const [customFieldMapping, setCustomFieldMapping] = useState<
    Record<number, string | undefined>
  >({});

  const previewRows = useMemo(() => rows.slice(0, 5), [rows]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setParseError(null);

    const { headers: parsedHeaders, rows: parsedRows } =
      await parseProductFile(file);
    if (parsedRows.length === 0) {
      throw new Error('El archivo no contiene datos.');
    }

    setHeaders(parsedHeaders);
    setRows(parsedRows);
    setFileName(file.name);
    setFieldMapping(autoMapProductFields(parsedHeaders));
    setCustomFieldMapping(
      autoMapCustomFields(customFieldDefinitions, parsedHeaders)
    );
  };

  const buildItems = (): CatalogProductBulkImportBodyParams['items'] => {
    return rows.map((row) =>
      buildProductPayload(
        row,
        fieldMapping,
        customFieldMapping,
        customFieldDefinitions
      )
    );
  };

  const validateRowLimit = (): string | null => {
    if (rows.length > MAX_ITEMS) {
      return `Máximo ${MAX_ITEMS} productos por importación. El archivo contiene ${rows.length}.`;
    }
    return null;
  };

  const reset = () => {
    setParseError(null);
    setFileName(null);
    setHeaders([]);
    setRows([]);
    setFieldMapping({});
    setCustomFieldMapping({});
  };

  const state: UseProductImportState = {
    fileName,
    headers,
    rows,
    previewRows,
    fieldMapping,
    customFieldMapping,
    parseError,
  };

  const actions: UseProductImportActions = {
    handleFileChange,
    reset,
    buildItems,
    validateRowLimit,
    setFieldMapping,
    setCustomFieldMapping,
    setParseError,
  };

  return [state, actions];
};
